/**
 * Felanmälningar att bedöma (#1653): shown at the top of the förvaltare's
 * portal. For each pending felanmälan they decide whether the cost is under
 * or över the threshold; under creates a work order delegated to them, över
 * leaves it to the styrelse. No reporter identity is shown.
 */
import React, { useState } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { Building, Calendar, Scale, Tag } from 'lucide-react';
import { IssueCostDecision, PortalData, PortalIssue } from '../../../types/guestAccess';
import { IssuePriority } from '../../../types/issues';
import { issuePriorityColors, issuePriorityLabels } from '../../../types/issuePriorityDisplay';
import { formatThreshold } from '../../../types/autoAssignment';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';
import ConfirmationModal from '../../../Components/ConfirmationModal';
import PhotoViewerModal from '../../../Components/PhotoViewerModal';
import { AssessIssueError, COMMENT_MAX_LENGTH, useAssessIssue } from '../hooks/useAssessIssue';

type IssueAssessment = NonNullable<PortalData['issueAssessment']>;

const CONSEQUENCE: Record<IssueCostDecision, string> = {
    below: 'Ett ärende skapas och tilldelas dig.',
    above: 'Felanmälan lämnas till styrelsen.',
};

const DONE: Record<IssueCostDecision, string> = {
    below: 'Ett ärende har skapats och tilldelats dig. Du hittar det under Ärenden.',
    above: 'Felanmälan har lämnats till styrelsen.',
};

const decisionLabel = (decision: IssueCostDecision, threshold: number) =>
    `${decision === 'below' ? 'Under' : 'Över'} ${formatThreshold(threshold)} kr exkl. moms`;

const isPriority = (p: string): p is IssuePriority => (Object.values(IssuePriority) as string[]).includes(p);

type PortalIssuePhoto = PortalIssue['photos'][number];

/**
 * A 72px tile showing the 200×200 thumbnail. A fresh upload may not have its
 * thumbnail yet, so a failed load switches to the original once. The viewer
 * always opens the original.
 */
const PhotoTile: React.FC<{ photo: PortalIssuePhoto; index: number; onOpen: (url: string) => void }> = ({ photo, index, onOpen }) => {
    const [src, setSrc] = useState(photo.thumbnailUrl ?? photo.url);
    return (
        <button
            type="button"
            className="p-0 border-0 bg-transparent"
            onClick={() => onOpen(photo.url)}
            aria-label={`Visa bild ${index + 1}`}
        >
            <img
                src={src}
                alt=""
                width={72}
                height={72}
                loading="lazy"
                style={{ objectFit: 'cover', borderRadius: 6 }}
                onError={() => { if (src !== photo.url) setSrc(photo.url); }}
            />
        </button>
    );
};

interface IssueItemProps {
    issue: PortalIssue;
    threshold: number;
    comment: string;
    onCommentChange: (comment: string) => void;
    onDecide: (decision: IssueCostDecision) => void;
    disabled: boolean;
    onOpenPhoto: (url: string) => void;
}

const IssueItem: React.FC<IssueItemProps> = ({ issue, threshold, comment, onCommentChange, onDecide, disabled, onOpenPhoto }) => (
    <Card className="mb-3" aria-label={`Felanmälan ${issue.buildingName}`}>
        <Card.Body>
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <div className="d-flex flex-column gap-1" style={{ minWidth: 0 }}>
                    <span className="fw-semibold d-flex align-items-center gap-1"><Building size={14} />{issue.buildingName}</span>
                    {issue.componentLabel && (
                        <small className="text-muted d-flex align-items-center gap-1"><Tag size={12} />{issue.componentLabel}</small>
                    )}
                    <small className="text-muted d-flex align-items-center gap-1"><Calendar size={12} />{`Inkom ${formatDateForDisplay(issue.createdAt)}`}</small>
                </div>
                {isPriority(issue.priority) && (
                    <Badge bg={issuePriorityColors[issue.priority]} className="flex-shrink-0">{issuePriorityLabels[issue.priority]}</Badge>
                )}
            </div>
            <p className="mb-2" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{issue.description}</p>
            {issue.photos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                    {issue.photos.map((photo, i) => (
                        <PhotoTile key={photo.key} photo={photo} index={i} onOpen={onOpenPhoto} />
                    ))}
                </div>
            )}
            <Form.Group className="mb-3" controlId={`assess-comment-${issue.key}`}>
                <Form.Label className="small">Kommentar (valfritt)</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    maxLength={COMMENT_MAX_LENGTH}
                    value={comment}
                    disabled={disabled}
                    onChange={(e) => onCommentChange(e.target.value)}
                />
            </Form.Group>
            <div className="d-flex flex-column flex-sm-row gap-2">
                <Button variant="outline-success" className="flex-fill" disabled={disabled} onClick={() => onDecide('below')}>
                    {decisionLabel('below', threshold)}
                </Button>
                <Button variant="outline-warning" className="flex-fill text-dark" disabled={disabled} onClick={() => onDecide('above')}>
                    {decisionLabel('above', threshold)}
                </Button>
            </div>
        </Card.Body>
    </Card>
);

interface GuestIssueAssessmentSectionProps {
    token: string;
    assessment: IssueAssessment;
    /** Re-read the portal after a decision or a "redan hanterad" refusal */
    onChanged: () => Promise<void>;
}

const GuestIssueAssessmentSection: React.FC<GuestIssueAssessmentSectionProps> = ({ token, assessment, onChanged }) => {
    const { assess, submitting } = useAssessIssue(token);
    const [comments, setComments] = useState<Record<string, string>>({});
    const [pending, setPending] = useState<{ issueKey: string; decision: IssueCostDecision } | null>(null);
    const [notice, setNotice] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);
    const [photoUrl, setPhotoUrl] = useState<string | null>(null);
    const { threshold, issues } = assessment;

    const handleConfirm = async () => {
        if (!pending) return;
        const { issueKey, decision } = pending;
        setNotice(null);
        try {
            await assess(issueKey, decision, comments[issueKey] ?? '');
            setPending(null);
            setComments(({ [issueKey]: _, ...rest }) => rest);
            setNotice({ variant: 'success', text: DONE[decision] });
            await onChanged();
        } catch (err) {
            setPending(null);
            const message = err instanceof Error ? err.message : 'Kunde inte spara bedömningen. Försök igen.';
            setNotice({ variant: 'danger', text: message });
            if (err instanceof AssessIssueError && err.alreadyHandled) await onChanged();
        }
    };

    return (
        <section className="guest-portal__section mb-4" aria-label="Felanmälningar att bedöma">
            <h6 className="d-flex align-items-center gap-2 mb-2">
                <Scale size={18} />
                <span>Felanmälningar att bedöma</span>
                {issues.length > 0 && <Badge bg="secondary">{issues.length}</Badge>}
            </h6>
            {notice && (
                <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)}>{notice.text}</Alert>
            )}
            {issues.length === 0 ? (
                <p className="text-muted small mb-0">Inga felanmälningar att bedöma.</p>
            ) : (
                <>
                    <p className="text-muted small">
                        {`Bedöm om kostnaden blir under eller över ${formatThreshold(threshold)} kr exkl. moms. Under gränsen tilldelas ärendet dig direkt; över gränsen beslutar styrelsen.`}
                    </p>
                    {issues.map((issue) => (
                        <IssueItem
                            key={issue.key}
                            issue={issue}
                            threshold={threshold}
                            comment={comments[issue.key] ?? ''}
                            onCommentChange={(c) => setComments((prev) => ({ ...prev, [issue.key]: c }))}
                            onDecide={(decision) => setPending({ issueKey: issue.key, decision })}
                            disabled={submitting}
                            onOpenPhoto={setPhotoUrl}
                        />
                    ))}
                </>
            )}

            <ConfirmationModal
                show={pending !== null}
                title={pending ? decisionLabel(pending.decision, threshold) : ''}
                message={pending ? CONSEQUENCE[pending.decision] : ''}
                confirmText="Bekräfta"
                variant="primary"
                onConfirm={() => { void handleConfirm(); }}
                onCancel={() => setPending(null)}
                loading={submitting}
            />
            <PhotoViewerModal show={photoUrl !== null} imageUrl={photoUrl ?? ''} onClose={() => setPhotoUrl(null)} />
        </section>
    );
};

export default GuestIssueAssessmentSection;
